import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { getUrlParams, getGoqiiUserId } from "../apiService/timespam";
import { lifestyleApi } from "../apiService/api";
import "./Theme.css";
import "./SkinMoodEntry.css";

const ANSWER_LABELS = {
  "1": "skinMoodEntry.label.itchiness",
  "2": "skinMoodEntry.label.pain",
  "3": "skinMoodEntry.label.redness",
  "4": "skinMoodEntry.label.sleeplessness",
};

const formatDate = (dateString) => {
  const d = new Date(dateString);
  const day = d.getDate();
  const month = d.toLocaleString("default", { month: "short" });
  const year = d.getFullYear().toString().slice(-2);
  return `${day}-${month}-${year}`;
};

const SkinMoodDetails = ({ skinMoodData, t }) => {
  if (!skinMoodData || skinMoodData.length === 0) return null;

  const reversed = [...skinMoodData].reverse();
  const grouped = reversed.reduce((acc, curr) => {
    const date = curr.createdTime.split(" ")[0];
    if (!acc[date]) acc[date] = [];
    acc[date].push(curr);
    return acc;
  }, {});

  return (
    <div className="sme-details">
      {Object.entries(grouped).map(([date, entries], gi) => (
        <React.Fragment key={gi}>
          <div className="sme-date-row">
            <span className="sme-date-label">{formatDate(date)}</span>
          </div>
          {entries.map((entry, ei) => (
            <div key={ei} className="sme-entry-row">
              <span className="sme-entry-label">
                {t(ANSWER_LABELS[entry.questionId] || entry.questionId)}:
              </span>
              <span className="sme-entry-value">{entry.answer}</span>
            </div>
          ))}
        </React.Fragment>
      ))}
    </div>
  );
};

const SkinMoodEntry = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { theme, lang } = getUrlParams();
  const urlParams = new URLSearchParams(window.location.search);
  const uuid = urlParams.get("uuid") || "";
  const goqiiUserId = getGoqiiUserId();
  const nonce = urlParams.get("nonce") || "";
  const apiKey = urlParams.get("apiKey") || "";
  const signature = urlParams.get("signature") || "";
  const goqiiAccessToken = urlParams.get("goqiiAccessToken") || "";
  const action = urlParams.get("action") || "";

  const [skinStats, setSkinStats] = useState([]);
  const [type, setType] = useState("");
  const [entryFound, setEntryFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    return new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000);
  });
  const [endDate, setEndDate] = useState(() => new Date());

  useEffect(() => {
    document.documentElement.className = theme === "dark" ? "dark" : "";
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [theme, lang]);

  useEffect(() => {
    lifestyleApi
      .fetchSkinMood()
      .then((res) => {
        if (res?.data?.code === 200) {
          const records = res.data.data.response || [];
          setSkinStats(records);
          setType(res.data.type || "");
          const today = new Date().toISOString().split("T")[0];
          setEntryFound(records.some((e) => e.createdTime.startsWith(today)));
        }
      })
      .catch((err) => console.error("fetchSkinMood:", err))
      .finally(() => setLoading(false));
  }, []);

  const buildQs = () =>
    `uuid=${uuid}&goqiiuserid=${goqiiUserId}&nonce=${nonce}&apiKey=${apiKey}&signature=${signature}&goqiiAccessToken=${goqiiAccessToken}&theme=${theme}&lang=${lang}`;

  const goToLog = () => {
    setShowModal(false);
    navigate(`/skin-mood?${buildQs()}`);
  };

  const onPrev = () => {
    setStartDate((d) => new Date(d.getTime() - 7 * 24 * 60 * 60 * 1000));
    setEndDate((d) => new Date(d.getTime() - 7 * 24 * 60 * 60 * 1000));
  };

  const onNext = () => {
    const today = new Date();
    const ns = new Date(startDate.getTime() + 7 * 24 * 60 * 60 * 1000);
    const ne = new Date(endDate.getTime() + 7 * 24 * 60 * 60 * 1000);
    setStartDate(ne > today ? startDate : ns);
    setEndDate(ne > today ? today : ne);
  };

  const canGoNext = !entryFound && endDate.toDateString() !== new Date().toDateString();

  const getDatesInRange = (start, end) => {
    const dates = [];
    let cur = new Date(start);
    while (cur <= end) {
      dates.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
    }
    return dates;
  };

  const chartOptions = useMemo(() => {
    const dateKeys = getDatesInRange(startDate, endDate).map(
      (d) => d.toISOString().split("T")[0]
    );

    const answersByDate = {};
    skinStats.forEach((item) => {
      const key = item.createdTime.split(" ")[0];
      if (!answersByDate[key]) answersByDate[key] = { Q1: null, Q2: null, Q3: null, Q4: null };
      answersByDate[key][`Q${item.questionId}`] = parseInt(item.answer, 10);
    });

    const q1 = dateKeys.map((k) => answersByDate[k]?.Q1 ?? null);
    const q2 = dateKeys.map((k) => answersByDate[k]?.Q2 ?? null);
    const q3 = dateKeys.map((k) => answersByDate[k]?.Q3 ?? null);
    const q4 = dateKeys.map((k) => answersByDate[k]?.Q4 ?? null);

    return {
      chart: { type: "line", height: 240, margin: [10, 0, 50, 42], backgroundColor: "rgba(0,0,0,0)" },
      title: { text: "" },
      credits: { enabled: false },
      xAxis: {
        categories: dateKeys,
        lineColor: "transparent",
        labels: {
          style: { fontSize: "10px", color: "var(--text-secondary,#888)" },
          formatter() {
            const d = new Date(this.value);
            return `${d.getDate()} ${d.toLocaleString("default", { month: "short" })}`;
          },
        },
      },
      yAxis: {
        title: { text: null },
        gridLineDashStyle: "dot",
        gridLineColor: "var(--border-subtle,#ddd)",
        labels: { style: { color: "var(--text-secondary,#888)" } },
      },
      plotOptions: {
        line: { dataLabels: { enabled: false }, enableMouseTracking: false, marker: { enabled: false } },
      },
      series: [
        { name: t("skinMoodEntry.label.sleeplessness"), data: q4, color: "#4098e3" },
        { name: t("skinMoodEntry.label.itchiness"),    data: q1, color: "#a2d8ff" },
        { name: t("skinMoodEntry.label.redness"),      data: q3, color: "#e67d1b" },
        { name: t("skinMoodEntry.label.pain"),         data: q2, color: "#f3d53a" },
      ],
      legend: { enabled: false },
    };
  }, [skinStats, startDate, endDate, t]);

  return (
    <>
      <section className="sme-page">
        <div className="sme-container">
          {loading ? (
            <div className="sme-loading">{t("skinMoodEntry.loading")}</div>
          ) : (
            <>
              {type === "nhs" && (
                <>
                  <div className="sme-date-nav">
                    <button className="sme-arrow-btn" onClick={onPrev}>&#8249;</button>
                    <span className="sme-date-range">
                      {startDate.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                      {" "}{t("skinMoodEntry.to")}{" "}
                      {endDate.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                    </span>
                    {canGoNext ? (
                      <button className="sme-arrow-btn" onClick={onNext}>&#8250;</button>
                    ) : (
                      <span className="sme-arrow-btn sme-arrow-placeholder" />
                    )}
                  </div>

                  <div className="sme-chart-box">
                    <h2 className="sme-chart-title">{t("skinMoodEntry.chartTitle")}</h2>
                    <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                    <ul className="sme-chart-legend">
                      <li><span className="sme-dot" style={{ background: "#4098e3" }} />{t("skinMoodEntry.label.sleeplessness")}</li>
                      <li><span className="sme-dot" style={{ background: "#a2d8ff" }} />{t("skinMoodEntry.label.itchiness")}</li>
                      <li><span className="sme-dot" style={{ background: "#e67d1b" }} />{t("skinMoodEntry.label.redness")}</li>
                      <li><span className="sme-dot" style={{ background: "#f3d53a" }} />{t("skinMoodEntry.label.pain")}</li>
                    </ul>
                  </div>
                </>
              )}

              {type !== "nhs" && (
                <p className="sme-info-text">{t("skinMoodEntry.infoText")}</p>
              )}

              <SkinMoodDetails skinMoodData={skinStats} t={t} />
            </>
          )}
        </div>
      </section>

      {action !== "N" && (
        <section className="sme-bottom-btn">
          <div className="sme-container">
            <button
              type="button"
              className="sme-btn-add"
              onClick={() => {
                if (entryFound) setShowModal(true);
                else goToLog();
              }}
            >
              {t("skinMoodEntry.addBtn")}
            </button>
          </div>
        </section>
      )}

      {showModal && (
        <div className="sme-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="sme-modal" onClick={(e) => e.stopPropagation()}>
            <p className="sme-modal-text">{t("skinMoodEntry.alreadyLoggedMsg")}</p>
            <div className="sme-modal-actions">
              <button className="sme-modal-cancel" onClick={() => setShowModal(false)}>
                {t("skinMoodEntry.cancel")}
              </button>
              <button className="sme-modal-update" onClick={goToLog}>
                {t("skinMoodEntry.update")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SkinMoodEntry;
